/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import { Search } from '@workboard/ui-search';
import { Modal, Button } from '@workboard/wobo-ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type KpiSearchModalProps = {
  initialValue?: any;
  onClose: (canceled?: boolean) => void;
  onSave: (selectedKpi: any) => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 130px;
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  color: #000;
  font-feature-settings:
    'liga' off,
    'clig' off;

  /* Typography/H5 */
  font-family: Lato;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%; /* 28.8px */
  letter-spacing: 0.15px;
`;

const Description = styled.div`
  color: #000;
  font-feature-settings:
    'liga' off,
    'clig' off;

  /* Typography/Body 1 */
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
  letter-spacing: 0.15px;
`;

const Label = styled.div`
  color: var(--Text-Secondary, rgba(10, 21, 55, 0.6));
  font-feature-settings:
    'liga' off,
    'clig' off;

  /* Typography/Body 1 */
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
  letter-spacing: 0.15px;
`;

const KpiIcon = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: var(--Indigo-50, #e8eaf6);
  padding: 8px;
`;

const StyledSearch = styled(Search)(({ theme }) => ({
  '& .MuiFormControl-root svg, & .MuiInputBase-root svg, & .MuiSvgIcon-root': {
    color: `${theme.palette.text.secondary} !important`,
  },

  '& .MuiInputBase-root': {
    height: '58px !important',
  },

  '& .MuiInputBase-input': {
    '&::placeholder': {
      color: `${theme.palette.text.secondary} !important`,
    },
  },
}));

const FooterContainer = styled.div`
  display: flex;
  padding: 16px;
  align-items: center;
  justify-content: end;
  gap: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

// Custom action buttons component to allow disabling the submit button
const FooterActionButtons = ({
  id,
  className,
  removeSubmit,
  onDismiss,
  onSubmit,
}: {
  id?: string;
  className?: string;
  removeSubmit?: boolean;
  onDismiss?: () => void;
  onSubmit?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <FooterContainer className={className} id={id}>
      <Button variant="ghost" data-testid="wbui-action-dismiss-button" onClick={onDismiss}>
        {t('Cancel')}
      </Button>
      <Button
        data-testid="wbui-action-submit-button"
        variant="primary"
        onClick={onSubmit}
        kind="normal"
        isDisabled={removeSubmit}
      >
        {t('Save')}
      </Button>
    </FooterContainer>
  );
};

export const KpiSearchModal = ({ initialValue, onClose, onSave }: KpiSearchModalProps) => {
  const { t } = useTranslation();
  const [selectedKpi, setSelectedKpi] = useState<any>(initialValue);
  const handleSelect = (kpi: any) => {
    setSelectedKpi(kpi);
  };

  const handleSave = () => {
    onSave(selectedKpi);
    onClose();
  };

  const removeSubmitDisabled = !selectedKpi || selectedKpi.id == initialValue?.id;

  return (
    <Modal
      isOpen
      title={
        <Title>
          <KpiIcon className="icon-kpi-pulse-16px" />
          {t('Select KPI')}
        </Title>
      }
      onDismiss={() => onClose(true)}
      onSubmit={handleSave}
      disableEnforceFocus
      disableAutoFocus
      style={{ zIndex: 1000000 }}
      PaperProps={{
        'aria-modal': true,
        'data-testid': 'wbui-modal',
        style: {
          width: '1000px',
        },
      }}
      actions={(footerProps) => (
        <FooterActionButtons
          {...footerProps}
          onDismiss={() => onClose(true)}
          onSubmit={handleSave}
          removeSubmit={removeSubmitDisabled}
        />
      )}
      removeSubmit={removeSubmitDisabled}
    >
      <Container>
        <Description>{t('Choose which existing KPI you want to measure progress from')}</Description>
        <Label>
          {t('Select KPI')}
        </Label>
        <StyledSearch
          dataType="kpi"
          placeholder={t('Search KPIs by name, owner, or team')}
          onSelect={(item) => handleSelect(item)}
          initialValue={selectedKpi}
          slotProps={{
            popper: {
              sx: {
                zIndex: 1000000,
              },
            },
          }}
        />
      </Container>
    </Modal>
  );
};
